/*
 * Before / After transformation, rebuilt as a 3D "portal" of two facing glass
 * panels with an animated transformation core between them: chaotic red energy
 * flows in from the fragile spreadsheet (left) and exits as clean green,
 * validated, secure logic (right). CSS/SVG + Framer Motion only.
 *
 * Z-index: glow/floor z-0, decor z-1, panels/text z-10. The resting 3D tilt is
 * gentle (9deg, lg+ only) and flattens on hover so text is always readable.
 */
import { X, Check, ShieldCheck, ArrowRight } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { ScrollReveal } from "@/components/motion/ScrollReveal";
import { useLang } from "@/i18n/LanguageContext";

const TransformCore = () => (
  <div className="relative hidden lg:flex items-center justify-center w-36 xl:w-48 shrink-0" aria-hidden="true">
    <svg viewBox="0 0 190 100" className="absolute inset-0 h-full w-full" fill="none">
      <defs>
        <linearGradient id="tflow-in" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#f43f5e" />
          <stop offset="1" stopColor="#3b82f6" />
        </linearGradient>
      </defs>
      {/* chaotic incoming streams from the fragile spreadsheet */}
      <path d="M0,24 C38,24 52,50 78,50" stroke="url(#tflow-in)" strokeWidth="2" className="trace-line" opacity="0.9" />
      <path d="M0,50 C38,50 52,50 78,50" stroke="url(#tflow-in)" strokeWidth="2" className="trace-line" opacity="0.9" />
      <path d="M0,76 C38,76 52,50 78,50" stroke="url(#tflow-in)" strokeWidth="2" className="trace-line" opacity="0.9" />
      {/* clean validated outgoing streams */}
      <path d="M112,50 C140,50 152,32 190,32" stroke="#10b981" strokeWidth="2.2" className="trace-line" opacity="1" />
      <path d="M112,50 C140,50 152,68 190,68" stroke="#10b981" strokeWidth="2.2" className="trace-line" opacity="1" />
    </svg>

    {/* travelling data packets: red chaos -> green validated */}
    <span className="packet" style={{ background: "var(--red)", boxShadow: "0 0 12px var(--red)" }} />
    <span className="packet" style={{ background: "var(--blue-bright)", boxShadow: "0 0 12px var(--blue)", animationDelay: "1.4s" }} />
    <span className="packet" style={{ background: "var(--green)", boxShadow: "0 0 12px var(--green-bright)", animationDelay: "2.8s" }} />

    {/* rotating, pulsing transformation core */}
    <div className="relative grid place-items-center h-[92px] w-[92px]">
      <span className="absolute inset-0 rounded-full border border-[var(--line-strong)] animate-spin-slow" />
      <span className="absolute inset-[7px] rounded-full border border-dashed border-[rgba(59,130,246,0.55)]" style={{ animation: "spin-slow 14s linear infinite reverse" }} />
      <span className="absolute inset-4 rounded-full border border-[rgba(16,185,129,0.6)] validate-pulse" />
      <span className="absolute h-16 w-16 rounded-full bg-[radial-gradient(circle,rgba(16,185,129,0.32),transparent_70%)] animate-glow" />
      <span className="relative grid place-items-center h-11 w-11 rounded-2xl panel text-[var(--green)] shadow-[0_0_40px_var(--glow-green)]">
        <ShieldCheck size={22} />
      </span>
    </div>
  </div>
);

export const TransformationSection = () => {
  const { t } = useLang();
  const tr = t.transformation;
  return (
    <section className="relative scroll-mt-24 py-24 lg:py-32 overflow-hidden">
      {/* Layer 0: ambient glows */}
      <div className="glow-orb animate-glow pointer-events-none" style={{ zIndex: 0, width: 460, height: 360, top: 40, left: "8%", background: "var(--red)", opacity: 0.1 }} />
      <div className="glow-orb animate-glow pointer-events-none" style={{ zIndex: 0, width: 520, height: 380, top: 0, right: "6%", background: "var(--green)", opacity: 0.14 }} />

      <div className="relative z-10 mx-auto max-w-7xl px-5">
        <SectionHeading eyebrow={tr.eyebrow} title={tr.title} intro={tr.intro} align="center" testId="transformation-heading" />

        <div className="transform-stage relative mt-16 grid lg:grid-cols-[1fr_auto_1fr] gap-6 lg:gap-2 items-stretch">
          {/* BEFORE — fragile spreadsheet */}
          <div className="tcard tcard-left relative glass-card rounded-3xl p-7 lg:p-8 border-[rgba(244,63,94,0.28)] overflow-hidden" data-testid="transformation-before">
            <div className="fracture-grid absolute inset-0 opacity-[0.5] pointer-events-none" style={{ zIndex: 0 }} />
            <svg className="absolute inset-0 h-full w-full pointer-events-none" style={{ zIndex: 0 }} fill="none" aria-hidden="true">
              <path d="M-20,60 L120,200 M40,-10 L260,260" stroke="var(--red)" strokeWidth="1" className="crack-line" />
            </svg>
            <div className="glow-orb pointer-events-none" style={{ zIndex: 0, width: 240, height: 200, top: -60, right: -40, background: "var(--red)", opacity: 0.16 }} />

            <div className="relative" style={{ zIndex: 10 }}>
              <div className="flex items-center gap-2.5 mb-6">
                <span className="grid place-items-center h-9 w-9 rounded-xl bg-[rgba(244,63,94,0.1)] text-[var(--red)] border border-[rgba(244,63,94,0.3)]">
                  <X size={18} />
                </span>
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--red)]">{tr.beforeLabel}</span>
                <span className="ml-auto font-mono text-[9px] uppercase tracking-widest text-[var(--red)]/70 border border-[rgba(244,63,94,0.3)] rounded-full px-2 py-0.5">unstable</span>
              </div>
              <ul className="space-y-3">
                {tr.before.map((b, i) => (
                  <ScrollReveal as="li" key={b} delay={i * 0.06} y={14} className="flex items-start gap-3 text-sm text-white/90">
                    <span className="grid place-items-center h-5 w-5 shrink-0 rounded-md bg-[rgba(244,63,94,0.12)] text-[var(--red)] mt-0.5"><X size={12} /></span>
                    {b}
                  </ScrollReveal>
                ))}
              </ul>
            </div>
          </div>

          {/* CORE */}
          <TransformCore />
          {/* mobile connector */}
          <div className="lg:hidden flex items-center justify-center">
            <span className="grid place-items-center h-11 w-11 rounded-full panel text-[var(--green)] shadow-[0_0_28px_var(--glow-green)] rotate-90">
              <ArrowRight size={20} />
            </span>
          </div>

          {/* AFTER — secure system */}
          <div className="tcard tcard-right relative panel rounded-3xl p-7 lg:p-8 border-[rgba(16,185,129,0.34)] overflow-hidden shadow-[0_40px_100px_-40px_var(--glow-green)]" data-testid="transformation-after">
            <div className="bg-grid bg-grid-fade absolute inset-0 opacity-30 pointer-events-none" style={{ zIndex: 0 }} />
            <span className="scan-line" style={{ zIndex: 1 }} />
            <div className="glow-orb pointer-events-none" style={{ zIndex: 0, width: 240, height: 200, top: -60, right: -40, background: "var(--green)", opacity: 0.18 }} />

            <div className="relative" style={{ zIndex: 10 }}>
              <div className="flex items-center gap-2.5 mb-6">
                <span className="grid place-items-center h-9 w-9 rounded-xl bg-[rgba(16,185,129,0.12)] text-[var(--green)] border border-[rgba(16,185,129,0.35)]">
                  <Check size={18} />
                </span>
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--green)]">{tr.afterLabel}</span>
                <span className="ml-auto inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-[var(--green)] border border-[rgba(16,185,129,0.4)] rounded-full px-2 py-0.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--green)] validate-pulse" /> validated
                </span>
              </div>
              <ul className="space-y-3">
                {tr.after.map((a, i) => (
                  <ScrollReveal as="li" key={a} delay={i * 0.06} y={14} className="flex items-start gap-3 text-sm text-white">
                    <span className="grid place-items-center h-5 w-5 shrink-0 rounded-md bg-[rgba(16,185,129,0.14)] text-[var(--green)] mt-0.5"><Check size={12} /></span>
                    {a}
                  </ScrollReveal>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* reflective floor */}
        <div className="transform-floor h-24 -mt-6 pointer-events-none" aria-hidden="true" />
      </div>
    </section>
  );
};
